import { useQuery } from "@tanstack/react-query";
import { useDynamicLocation } from "apps/commons/hooks/useDynamicLocation";
import { TanstackQueryKey } from "apps/commons/tanstackQueryKey";
import { useOnboardingPageContextAtomState } from "apps/oneOnboarding/onboardingPageContextAtom";
import { pictureTypeMapper } from "apps/oneOnboarding/photos/constants";
import { useSidebarHandler } from "apps/oneOnboarding/sidebar/useSidebarHandler";
import { oneOnboardingApi } from "libs/api";
import type { BulkOnboardingData } from "libs/codegen/property-onboarding";
import isEmpty from "lodash/isEmpty";
import { useParams } from "router";
import type { BulkOnboardSession } from "./BulkOnboardSession";

const bulkPublishPath = "/:lang/list-my-property/onboard/batch/publish";

export const useGetBulkOnboardingDataApi = () => {
    const { lang } = useParams("/:lang/list-my-property/onboard/batch");
    const { pathname } = useDynamicLocation();
    const handleSidebar = useSidebarHandler();

    const [metadata, setMetadata] = useOnboardingPageContextAtomState();
    const { data, isError, refetch, isFetching } = useQuery({
        queryKey:
            pathname === bulkPublishPath
                ? [TanstackQueryKey.GetBulkOnboardingData, lang, pathname]
                : [TanstackQueryKey.GetBulkOnboardingData, lang],
        queryFn: () => {
            const onboardSessionStorage = sessionStorage.getItem("bulkOnboardingSession");

            if (onboardSessionStorage === null) throw new Error("Session data not found");

            const onboardSession: BulkOnboardSession = JSON.parse(onboardSessionStorage);

            return oneOnboardingApi
                .getBulkOnboardingData(lang, pathname, {
                    currentPath: pathname,
                    sessionId: onboardSession.sessionId,
                    bulkPropertyKeys: onboardSession.properties.map((property) => ({
                        propertyId: Number(property.propertyId),
                        onboardingId: property.onboardingId,
                    })),
                })
                .then((res) => {
                    let data: BulkOnboardingData = res.data;

                    data.propertyDataList?.forEach((propertyData) => {
                        // if property description is empty, then force to null
                        if (isEmpty(propertyData.propertyDescription)) propertyData.propertyDescription = undefined;

                        // if all photos does not have MainPhoto type, then make the first one become one
                        if (
                            propertyData.photos.length > 0 &&
                            propertyData.photos.every((photo) => photo.pictureTypeId !== pictureTypeMapper.mainPhoto)
                        )
                            propertyData.photos[0].pictureTypeId = pictureTypeMapper.mainPhoto;

                        // weekly, monthly promotion if backend send as null means no value, then default to undefined
                        if (propertyData.weeklyDiscount === -1) propertyData.weeklyDiscount = undefined;
                        if (propertyData.monthlyDiscount === -1) propertyData.monthlyDiscount = undefined;

                        if (propertyData.nightlyRate === 0) propertyData.nightlyRate = undefined;

                        if (propertyData.taxInformation === null) propertyData.taxInformation = undefined;

                        if (propertyData.firstAllotment === null) propertyData.firstAllotment = undefined;
                    });

                    return data;
                });
        },
        onSuccess: (data) => {
            handleSidebar(data.sidebarItemList, true);
            setMetadata(data.pageContext);
        },
    });

    return {
        data,
        metadata,
        isLoading: isFetching,
        isError,
        refetch,
    };
};
